import { View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import Ionicons from '@expo/vector-icons/Ionicons'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useBadges } from '../../lib/useBadges'
import { countEarned, countTotal, nearGoals, recentEarned, tierColor } from '../../lib/badges'
import { Card, SectionLabel } from '../ui'
import BadgeMedal from './BadgeMedal'
import { useBadgeText } from './BadgeCard'

// Riepilogo dei tuoi badge nella scheda Statistiche della lega: quanti, gli ultimi ottenuti, i traguardi vicini. Apre l'album.
// Non compare se la lega non ha i badge accesi.
export default function BadgeSummaryCard({ leagueId, userId }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const router = useRouter()
  const txt = useBadgeText()
  const { loading, enabled, families } = useBadges(leagueId, userId)
  if (loading || !enabled || families.length === 0) return null

  const earned = countEarned(families)
  const total = countTotal(families)
  const pct = total > 0 ? Math.round((earned / total) * 100) : 0
  const recent = recentEarned(families, 5)
  const goals = nearGoals(families, 2)

  return (
    <Card>
      <TouchableOpacity
        onPress={() => router.push('/Leagues/badges')}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityLabel={`${t('badges.title')}: ${t('badges.earned_of', { earned, total })}. ${t('badges.open_album')}`}
      >
        <View style={s.head}>
          <SectionLabel>{t('badges.title')}</SectionLabel>
          <View style={s.open}>
            <Text style={[s.openText, { color: tc.rosaText }]}>{t('badges.open_album')}</Text>
            <Ionicons name="chevron-forward" size={14} color={tc.rosaText} />
          </View>
        </View>

        <View style={s.sumRow}>
          <Text style={[s.big, { color: tc.text }]}>{earned}<Text style={[s.tot, { color: tc.textMuted }]}> / {total}</Text></Text>
          <Text style={[s.pct, { color: tc.rosaText }]}>{pct}%</Text>
        </View>
        <View style={[s.barBg, { backgroundColor: tc.border }]}>
          <View style={[s.barFill, { backgroundColor: tc.rosa, width: `${pct}%` }]} />
        </View>

        {recent.length > 0 && (
          <View style={s.block}>
            <Text style={[s.label, { color: tc.textMuted }]}>{t('badges.recent')}</Text>
            <View style={s.medals}>
              {recent.map(({ family, level }) => (
                <BadgeMedal key={level.id_badge} icon={family.icon} color={tierColor(level.level, family.maxLevel)} size={44} />
              ))}
            </View>
          </View>
        )}

        {goals.length > 0 && (
          <View style={s.block}>
            <Text style={[s.label, { color: tc.textMuted }]}>{t('badges.next_goals')}</Text>
            {goals.map(f => {
              const color = tierColor(f.next.level, f.maxLevel)
              return (
                <View key={f.category} style={s.goal}>
                  <BadgeMedal icon={f.icon} color={color} size={36} state="locked" />
                  <View style={{ flex: 1, gap: 4 }}>
                    <View style={s.goalRow}>
                      <Text style={[s.goalTitle, { color: tc.text }]} numberOfLines={1}>{txt.levelTitle(f, f.next)}</Text>
                      <Text style={[s.goalVal, { color: tc.textMuted }]}>{f.value} / {f.next.threshold}</Text>
                    </View>
                    <View style={[s.barBg, { backgroundColor: tc.border }]}>
                      <View style={[s.barFill, { backgroundColor: color, width: `${Math.round(f.ratio * 100)}%` }]} />
                    </View>
                  </View>
                </View>
              )
            })}
          </View>
        )}
      </TouchableOpacity>
    </Card>
  )
}

const s = StyleSheet.create({
  head: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, minHeight: 28 },
  open: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  openText: { fontSize: 13, fontFamily: fonts.condensedBold },
  sumRow: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 },
  big: { fontFamily: fonts.condensedBold, fontSize: 30 },
  tot: { fontFamily: fonts.condensedBold, fontSize: 18 },
  pct: { fontFamily: fonts.condensedBold, fontSize: 18 },
  barBg: { height: 5, borderRadius: 3, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 3 },
  block: { marginTop: 16, gap: 8 },
  label: { fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: fonts.condensedBold },
  medals: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  goal: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  goalRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 8 },
  goalTitle: { flex: 1, fontSize: 14, fontFamily: fonts.condensedBold },
  goalVal: { fontSize: 12 },
})
